import { observer } from 'mobx-react'
import { useContext, useEffect, useState } from 'react'
import type { ReactNode } from 'react'
import { LoadingSpinner } from '@/components/Generic'
import { StoreContext } from '@store/Root.store'
import { axiosInstance } from '@/methods/axiosConfig'
import { API_URL, JOINTSIGNING_SERVICE_PATH } from '@/constants/apiUrl'
import { devLog } from '@/methods/devLog'
import { forceRedirect } from '@/methods/forceRedirect'
import { VerifiedErrorEnpalPage } from '../VerifiedErrorEnpalPage'
import { SuccessMultiSignerPendingPage } from './SuccessMultiSignerPendingPage'
import { SuccessFinishForNowPage } from './SuccessFinishForNowPage'
import { SuccessCongratulationsPage } from './SuccessCongratulationsPage'
import { getVerifiedErrorReason } from '@/constants/verifiedTypes'
interface GetJointSigningContextResponse {
  signatoriesStatus: string
  isCounterSigningRequired: boolean
  isCounterSigningReady: boolean
  waitingOnSignatories: Array<{
    id: string
    status: string
    firstName?: string
    lastName?: string
    nextSignatoryUrl?: string
  }>
  enableNextSignatory?: boolean
}

interface SuccessPageProps {
  isSes?: boolean
  /**
   * Screen shown once nothing is pending from other signatories. Defaults to the Enpal
   * congratulations page; the generic `/success` route passes its own completion content while
   * reusing the next-signatory hand-over above it.
   */
  completedScreen?: ReactNode
}

export const SuccessPage = observer(({ isSes, completedScreen }: SuccessPageProps) => {
  const store = useContext(StoreContext)
  const { successPage: trans, congratulationsPage: congratulationsTrans } =
    store.TranslationsState.translations

  const [btnLoading, setBtnLoading] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [context, setContext] = useState<GetJointSigningContextResponse | null>(null)
  const [fullName, setFullName] = useState<string>('')
  const [nextSignatoryUrl, setNextSignatoryUrl] = useState<string | null>(null)
  const [finishForNow, setFinishForNow] = useState(false)

  const location = window.location.href
  const url = new URL(location)
  const isError = url.searchParams.get('error')
  const errorReason = url.searchParams.get('errorReason')

  const getContext = async () => {
    try {
      const { data } = await axiosInstance.get<GetJointSigningContextResponse>(
        `${API_URL}/${JOINTSIGNING_SERVICE_PATH}/get-context`,
        {
          withCredentials: true,
        }
      )

      setContext(data)
      if (data?.waitingOnSignatories.length) {
        setFinishForNow(!data?.enableNextSignatory)
        setFullName(
          `${data?.waitingOnSignatories[0].firstName} ${data?.waitingOnSignatories[0].lastName}`
        )

        if (data?.waitingOnSignatories[0].nextSignatoryUrl) {
          setNextSignatoryUrl(data?.waitingOnSignatories[0].nextSignatoryUrl)
        }
      }
    } catch (error) {
      devLog('Error fetching joint signing context:', error)
    }

    setIsLoading(false)
  }

  const handleProceed = () => {
    if (!btnLoading && nextSignatoryUrl) {
      setBtnLoading(true)

      forceRedirect(nextSignatoryUrl)
    }
  }

  const handleFinishForNow = () => setFinishForNow(true)

  useEffect(() => {
    sessionStorage.removeItem('finalTimestamp')
    store.InterfaceState.setSendBeforeunloadEvent(false)

    void getContext()
  }, [])

  if (isError)
    return <VerifiedErrorEnpalPage errorReason={getVerifiedErrorReason(isError, errorReason)} />

  if (isLoading) {
    return (
      <div className="page-container mobile-m-10">
        <LoadingSpinner />
      </div>
    )
  }

  if (!isLoading && context?.waitingOnSignatories?.length && !finishForNow) {
    return (
      <SuccessMultiSignerPendingPage
        trans={trans}
        fullName={fullName}
        handleProceed={handleProceed}
        handleFinishForNow={handleFinishForNow}
        btnLoading={btnLoading}
      />
    )
  }

  if (!isLoading && context?.waitingOnSignatories?.length && finishForNow) {
    return <SuccessFinishForNowPage trans={trans} fullName={fullName} />
  }

  return (
    completedScreen ?? <SuccessCongratulationsPage trans={congratulationsTrans} isSes={isSes} />
  )
})
